import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Scale } from 'lucide-react';
import CTA from '../../components/CTA';

const DerryWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Loss Hypnotherapy Derry | Mindful Eating Londonderry</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Derry/Londonderry. Online sessions recommended for convenience. Address emotional eating, stress eating patterns, sustainable change. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Derry, mindful eating Londonderry, emotional eating Derry, weight management Derry, stress eating Londonderry" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/derry-weight-management/" />
        <meta property="og:title" content="Weight Loss Hypnotherapy Derry | Mindful Eating Londonderry" />
        <meta property="og:description" content="Weight management hypnotherapy serving Derry/Londonderry. Address emotional eating, stress eating patterns, sustainable lifestyle change." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Derry",
            "description": "CNHC registered weight management hypnotherapy serving Derry/Londonderry via online sessions and Belfast clinic. Specializing in emotional eating, stress eating patterns, and sustainable lifestyle change.",
            "url": "https://www.sharonclare-hypnotherapy.com/derry-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Derry",
              "alternateName": "Londonderry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressRegion": "County Down",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Loss Hypnotherapy Serving Derry/Londonderry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Address emotional eating and stress eating patterns with compassionate, evidence-based support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">30 miles</span> from Derry via A6 • <span className="font-semibold">35-40 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Derry residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy for Derry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Derry/Londonderry community through convenient online sessions and two in-person clinic locations. The Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ, and the Lisburn clinic is at The Arzen Collective, 13B Antrim Street, with days and times that vary and early evening appointments on offer. Online sessions provide exactly the same professional, evidence-based treatment from the comfort of your home without any travel required. Most Derry clients choose online sessions for the practical convenience and time savings.
              </p>
              <p>
                Weight struggles in Derry often connect to deeper issues of stress and emotional wellbeing. As a border city with economic pressures and the ongoing legacy of post-conflict stress, many people find themselves turning to food for comfort, stress relief, or emotional numbing. If you've noticed that you eat more when you're stressed, anxious, worried about money, or dealing with difficult emotions, you're not alone - and this isn't about willpower. It's about addressing the underlying stress and emotional patterns that drive eating behaviors.
              </p>
              <p>
                Solution-focused hypnotherapy for weight management works differently from diets. Instead of focusing on restriction and willpower, we address why you eat when you're not physically hungry. By calming your stress response, building emotional resilience, and changing your relationship with food at a subconscious level, you can achieve sustainable weight loss without the cycle of deprivation and rebound that characterizes most diets. My approach is compassionate, non-judgmental, and focused on long-term lifestyle change rather than quick fixes.
              </p>
              <p>
                Online sessions are ideal for maintaining consistency, especially when juggling work, family responsibilities, or simply preferring the privacy and comfort of your own space. The therapeutic effectiveness is identical to in-person sessions, and many Derry clients appreciate being able to attend from home without travel time or logistics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Approach Works */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Hypnotherapy Works for Weight Management
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addresses Emotional Eating
                </h3>
                <p className="text-gray-700">
                  When stress, anxiety, or low mood trigger eating, it's your subconscious mind running old patterns. Hypnotherapy accesses that subconscious level, breaking the connection between difficult emotions and eating for comfort. You learn healthier ways to manage stress that don't involve food.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Calms Stress Response
                </h3>
                <p className="text-gray-700">
                  When your body is stuck in stress mode, hunger hormones increase, cravings intensify, and your brain becomes hyper-focused on high-calorie foods. By teaching your nervous system to switch off the stress response, hypnotherapy naturally reduces cravings and emotional eating urges.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Scale className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Change
                </h3>
                <p className="text-gray-700">
                  Diets work short-term but fail long-term because they rely on willpower and restriction. Hypnotherapy creates lasting change by addressing the root causes of overeating - stress, emotional patterns, and subconscious beliefs about food. The changes feel natural, not forced.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Derry Context */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Stress Eating in Derry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As someone who has worked extensively with Northern Ireland communities, I understand the specific stressors that contribute to emotional eating patterns in Derry. The economic pressures facing the city, the ongoing impact of post-conflict trauma, and the uncertainty around Brexit and the border all create chronic background stress that can manifest as stress eating.
              </p>
              <p>
                Stress eating isn't weakness - it's your nervous system trying to self-soothe. When you're dealing with financial worry, work insecurity, family pressures, or the lingering effects of historical trauma, food becomes a reliable source of comfort. The problem is that emotional eating creates a cycle: stress leads to eating, which leads to weight gain, which leads to more stress and shame, which leads to more eating.
              </p>
              <p>
                Many Derry clients tell me they know what to eat and what not to eat - information isn't the problem. The problem is that when stress hits, all that knowledge goes out the window and you find yourself eating without even being aware you're doing it. This is because stress eating is driven by your subconscious mind and your body's stress response, not by conscious choice.
              </p>
              <p>
                Hypnotherapy breaks this cycle by addressing the stress at its root. When your nervous system is calm, cravings naturally reduce. When you have healthier ways to manage difficult emotions, food loses its power as an emotional crutch. When you believe at a deep, subconscious level that you can change, sustainable weight loss becomes possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What to Expect from Weight Management Hypnotherapy
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Initial Consultation (90 minutes)
                </h3>
                <p className="text-gray-700 mb-4">
                  We'll explore your weight history, eating patterns, stress triggers, and goals. I'll explain how stress and emotions drive eating behaviors, and how hypnotherapy works at a neurological level to create change. You'll also experience your first hypnotherapy session, leaving feeling calm and motivated.
                </p>
                <p className="text-gray-700">
                  There's no judgment here - only understanding and evidence-based support. Many Derry clients express relief at finally being understood, rather than being told they just need more willpower.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Ongoing Sessions (4-8 weeks)
                </h3>
                <p className="text-gray-700 mb-4">
                  Most clients benefit from 4-8 sessions, typically weekly or fortnightly. Each session builds on the last, gradually reprogramming your subconscious patterns around food, stress, and emotional eating. Between sessions, you'll use audio recordings to reinforce the positive changes.
                </p>
                <p className="text-gray-700">
                  You'll notice changes fairly quickly - reduced cravings, better awareness of hunger and fullness, more control around trigger foods, improved sleep (which dramatically affects weight), and a calmer relationship with food and your body.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Long-Term Sustainable Change
                </h3>
                <p className="text-gray-700">
                  The goal isn't just weight loss - it's changing your relationship with food permanently. When you address the emotional and stress-related drivers of overeating, weight loss becomes a natural side effect of feeling better and managing stress more effectively. This approach creates lasting change rather than temporary fixes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Derry Area Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd been stress eating for years, especially since the economic uncertainty hit. Sharon helped me understand why I was eating when I wasn't even hungry. After 6 sessions, I've lost 2 stone and actually kept it off. The drive from Derry was worth every minute."
                </p>
                <p className="font-semibold text-gray-900">A.C., Derry City - Weight Management</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Online sessions were perfect for me. I could do them from home in Waterside after the kids were in bed. Sharon's approach isn't about dieting - it's about understanding your emotional eating patterns. I've finally broken the cycle of losing and regaining the same weight."
                </p>
                <p className="font-semibold text-gray-900">M.K., Waterside, Derry - Emotional Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The financial stress in Derry is real, and I was comfort eating constantly. Sharon helped me find healthier ways to cope with stress. I've lost 3 stone over 8 months and it feels sustainable this time - not like the crash diets I'd tried before."
                </p>
                <p className="font-semibold text-gray-900">P.D., Limavady - Stress Eating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Derry Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with emotional eating and stress eating?
                </h3>
                <p className="text-gray-700">
                  Yes, this is exactly where hypnotherapy excels. Emotional eating and stress eating are driven by your subconscious mind and your body's stress response, not by conscious choice. Hypnotherapy works at that subconscious level, breaking the automatic connection between difficult emotions and eating for comfort. By calming your nervous system and teaching you healthier stress management strategies, the urge to eat emotionally naturally reduces. Many Derry clients find that addressing the stress in their lives - whether financial, work-related, or linked to historical trauma - is the key to finally resolving their weight struggles.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is this different from a diet plan?
                </h3>
                <p className="text-gray-700">
                  Completely different. Diets focus on what you eat - restriction, calorie counting, food rules. They work short-term but fail long-term because they rely on willpower, which is a limited resource. Hypnotherapy addresses why you eat when you're not physically hungry. By resolving the emotional and stress-related drivers of overeating, you naturally make better food choices without feeling deprived or restricted. The changes happen at a subconscious level, so they feel natural rather than forced. This creates sustainable, long-term weight loss without the cycle of deprivation and rebound that characterizes dieting.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-8 sessions, typically scheduled weekly or fortnightly. The exact number depends on your individual situation - how long you've struggled with weight, how much stress you're dealing with, and how deeply entrenched your emotional eating patterns are. We'll assess your progress as we go and adjust accordingly. I offer a discounted 4-session package which many Derry clients find provides excellent value, especially when factoring in the journey from Derry.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you work with NHS weight management services?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS weight management services, not a replacement. If you're already working with NHS dietitians or weight management programmes, hypnotherapy can enhance that support by addressing the psychological and emotional aspects of weight loss that medical programmes often can't address in depth. Many Derry clients use hypnotherapy alongside NHS care. However, I don't provide dietary advice or meal plans - my focus is on the mindset, emotional, and stress-related factors that drive eating behaviors.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can online sessions really work as well as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Online hypnotherapy sessions via video call are just as effective as face-to-face sessions. The hypnotherapy process works equally well through a screen, and many clients actually find online sessions more convenient and comfortable - you're in your own safe space, there's no travel time, and you can schedule sessions around work and family commitments more easily. For Derry clients juggling busy lives, online sessions often provide the consistency needed for successful weight management without any travel pressure.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will I be guaranteed to lose a certain amount of weight?
                </h3>
                <p className="text-gray-700">
                  I never make guarantees about specific weight loss amounts because everyone is different. What I can promise is that we'll address the underlying emotional and stress-related patterns driving your eating behaviors, and teach your nervous system healthier ways to cope with stress. When those patterns change, weight loss becomes a natural consequence. Most clients find that by focusing on the process - reducing stress eating, improving sleep, building healthier habits - the weight loss follows naturally without the obsessive focus on numbers that characterizes dieting. This creates more sustainable, maintainable results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default DerryWeightManagement;
